import React, { useEffect, useMemo, useState } from 'react'
import { FiAlertCircle, FiBarChart2, FiInfo, FiCheckCircle, FiEye, FiHeart, FiRepeat, FiStar, FiUser } from 'react-icons/fi'
import { TbDiamond } from 'react-icons/tb'

import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import { Spinner } from 'common/components/Spinner/Spinner'
import { getSavedMonth, useGetContentStatsMutation } from './contentStatsApi'
import {
  getChange,
  getDefaultMonth,
  getAvailableTypes,
  getFavoriteLists,
  getFinishRate,
  getLeaderboards,
  getMonthLabel,
  getMonthOptions,
  getPreviousMonthName,
  getProgramLists,
  getRowScores,
  getScoreBands,
  getSavedState,
  getSessionTotals,
  getTakeaways,
  getTotals,
  hasFavoriteData,
  getVisibleRows,
  isCurrentMonth,
  isMonthReady,
  MIN_ITEMS_FOR_TAB,
  MONTH_READY_DAY,
  VIDEO_FUNNEL_NOTE,
  showsVideoFunnelNote,
  showsChange,
} from './contentStatsUtils'

const TYPES = [
  { key: 'workout', label: 'Workouts' },
  { key: 'video', label: 'Videos' },
  { key: 'program', label: 'Programs' },
]

// Each measure keeps one icon everywhere it appears
const ICONS = {
  opens: FiEye,
  finishes: FiCheckCircle,
  repeats: FiRepeat,
  members: FiUser,
  perMember: FiBarChart2,
  favorites: FiHeart,
}

const pillClasses = (active) =>
  `px-4 py-2 rounded-full text-sm font-semibold border ${
    active ? 'bg-tBlack text-white border-tBlack' : 'bg-white text-tBlack border-gray-200 hover:border-tBlack'
  }`

function ContentStats() {
  const { userId } = useAuth()
  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''

  const monthOptions = useMemo(() => getMonthOptions(), [])
  const [month, setMonth] = useState(() => getDefaultMonth())
  const [chosenType, setType] = useState('workout')
  const isReady = isMonthReady(month)
  const stats = useMonthStats(isReady ? coachOrgId : '', month)
  // While loading every tab shows; once loaded, only tabs with enough activity
  const availableTypes = Array.isArray(stats.rows) ? getAvailableTypes(stats, month) : TYPES.map((t) => t.key)
  const type = availableTypes.includes(chosenType) ? chosenType : availableTypes[0]
  const monthName = getMonthLabel(month).split(' ')[0]

  return (
    <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16'>
      <div className='w-full max-w-[1050px] mx-auto mt-6 md:mt-12 mb-16 flex flex-col gap-4'>
        <div className='flex flex-wrap items-start justify-between gap-4 mb-2'>
          <div className='flex flex-col gap-2'>
            <h1 className='font-bold text-5xl text-tBlack'>Content Stats</h1>
            <p className='text-base text-gray-500 max-w-[680px]'>
              Which workouts and videos your members open, finish, and come back to.
            </p>
          </div>
          <label className='sr-only' htmlFor='content-stats-month'>
            Month
          </label>
          <select
            id='content-stats-month'
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className='h-10 rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-tBlack cursor-pointer'
          >
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {isReady ? (
          <>
            <ShowsOrApologizes key={`all-${month}`}>
              <AllContent stats={stats} />
            </ShowsOrApologizes>
            <div className='flex flex-wrap items-center justify-between gap-3 mt-2'>
              <div role='tablist' aria-label='Content type' className='flex gap-2'>
                {TYPES.filter((option) => availableTypes.includes(option.key)).map((option) => (
                  <button
                    key={option.key}
                    type='button'
                    role='tab'
                    aria-selected={type === option.key}
                    className={pillClasses(type === option.key)}
                    onClick={() => setType(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <ShowsOrApologizes key={`${type}-${month}`}>
              {type ? (
                <StatsBody stats={stats} month={month} type={type} />
              ) : (
                <NotEnoughData
                  className='bg-white'
                  text={`Stats show once members use at least ${MIN_ITEMS_FOR_TAB} of your workouts, videos or programs in a month.`}
                />
              )}
            </ShowsOrApologizes>
          </>
        ) : (
          <p className='bg-white rounded-2xl px-7 py-5 text-tBlack'>
            {monthName}&apos;s stats will be ready on {monthName} {MONTH_READY_DAY}. The first days of a month have too
            little data to be useful.
          </p>
        )}
      </div>
    </div>
  )
}

// A section that fails to render shows a short message instead of blanking the page. It resets when
// the coach picks another month or tab, through its key.
class ShowsOrApologizes extends React.Component {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error) {
    console.error('ContentStats: could not show a section', error)
  }

  render() {
    if (!this.state.failed) return this.props.children
    return <p className='bg-white rounded-2xl px-7 py-5 text-tBlack'>We could not show this. Try again in a minute.</p>
  }
}

// stats.rows is undefined while loading and null when the call failed. stats.loading says what kind
// of load is running. A month already saved and current shows straight from the database; anything
// else goes through the cloud function. Refetches when the month changes.
function useMonthStats(coachOrgId, month) {
  const [getContentStats] = useGetContentStatsMutation()
  const [stats, setStats] = useState({ rows: undefined, loading: 'checking' })

  useEffect(() => {
    if (!coachOrgId) return undefined
    let ignore = false
    setStats({ rows: undefined, loading: 'checking' })
    ;(async () => {
      const saved = await getSavedMonth(coachOrgId, month)
      if (ignore) return
      const state = getSavedState(saved)
      if (state === 'fresh') {
        setStats({ month, ...saved, rows: saved.rows || [], programs: saved.programs || [] })
        return
      }
      setStats({ rows: undefined, loading: state === 'stale' ? 'refresh' : 'first' })
      const result = await getContentStats({ coachOrgId, month })
      if (ignore) return
      if (result?.error || !result?.data) {
        setStats({ rows: null, errorCode: result?.error?.code || null, errorMessage: result?.error?.message || null })
        return
      }
      setStats(result.data)
    })()
    return () => {
      ignore = true
    }
  }, [coachOrgId, getContentStats, month])

  return stats
}

const LOADING_TEXT = {
  checking: (label) => [`Loading ${label}`, null],
  first: (label) => [`Loading ${label} for the first time`, 'This can take up to 1 minute.'],
  refresh: () => ['Downloading the latest data', 'This can take up to 10 seconds.'],
}

function IconCircle({ icon: Icon, size = 'md' }) {
  const box = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8'
  return (
    <span className={`${box} shrink-0 rounded-full bg-[#E3F5EC] text-[#14895F] flex items-center justify-center`}>
      <Icon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} strokeWidth={1.9} aria-hidden='true' />
    </span>
  )
}

// Workouts and videos together, so it sits above the tabs and doesn't change with them
function AllContent({ stats }) {
  if (!stats.rows) return null
  const { members, sessions, perMember } = getSessionTotals(stats)
  if (!members) return null
  const cells = [
    { icon: ICONS.members, value: members.toLocaleString(), label: 'members finished a session' },
    { icon: ICONS.finishes, value: sessions.toLocaleString(), label: 'sessions finished' },
    { icon: ICONS.perMember, value: perMember.toFixed(1), label: 'sessions per member' },
  ]
  return (
    <section
      aria-label='All content'
      className='bg-white rounded-2xl grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-offWhite'
    >
      {cells.map((cell) => (
        <div key={cell.label} className='flex items-center gap-3 px-5 py-4'>
          <IconCircle icon={cell.icon} />
          <span>
            <span className='block text-[26px] leading-none font-bold text-tBlack tabular-nums'>{cell.value}</span>
            <span className='block text-xs text-gray-500 mt-1'>{cell.label}</span>
          </span>
        </div>
      ))}
    </section>
  )
}

function StatsBody({ stats, month, type }) {
  const typeName = { workout: 'workouts', video: 'videos', program: 'programs' }[type]

  if (stats.rows === undefined) {
    return (
      <LoadingCard text={(LOADING_TEXT[stats.loading] || LOADING_TEXT.checking)(getMonthLabel(month))} />
    )
  }
  if (stats.rows === null) {
    const busy = stats.errorCode === 'functions/resource-exhausted'
    return (
      <p className='bg-white rounded-2xl px-7 py-5 text-tBlack'>
        {busy
          ? 'Content stats are busy right now. Try again in a few minutes.'
          : 'We could not load this month. Try again in a minute.'}
      </p>
    )
  }

  if (type === 'program') {
    const lists = getProgramLists(stats.programs || [])
    const showsDays = lists.some((list) => list.key === 'programDays' && list.items.length)
    return (
      <div className='flex flex-col gap-4'>
        {lists.some((list) => list.items.length) ? (
          <div className='grid gap-4 md:grid-cols-2'>
            {lists.map((list) => (
              <Leaderboard key={list.key} list={list} />
            ))}
          </div>
        ) : (
          <EmptyMonth typeName={typeName} month={month} />
        )}
        <HowWeCount
          items={[
            'Members: people who finished at least one workout in the program.',
            showsDays &&
              'Days per member: on average, how many days each member worked out in the program. Only showing programs with activity from 20+ members.',
            "Rest days aren't counted.",
          ]}
        />
      </div>
    )
  }

  const rows = getVisibleRows(stats.rows, type, month)
  const leaderboards = getLeaderboards(rows, type)
  if (!leaderboards.some((list) => list.items.length)) return <EmptyMonth typeName={typeName} month={month} />
  const bands = getScoreBands(rows, type)
  const favoriteLists = hasFavoriteData(month) ? getFavoriteLists(rows, type) : []
  const item = type === 'video' ? 'video' : 'workout'

  return (
    <div className='flex flex-col gap-4'>
      {showsVideoFunnelNote(type, month) && (
        <p className='flex items-start gap-2 rounded-xl bg-white/60 border border-gray-200 px-4 py-3 text-xs text-gray-500'>
          <FiInfo className='w-4 h-4 shrink-0 text-gray-400' aria-hidden='true' />
          {VIDEO_FUNNEL_NOTE}
        </p>
      )}
      <Funnel stats={stats} rows={rows} type={type} month={month} />
      <Takeaways takeaways={getTakeaways(rows, type)} type={type} />
      {(bands.finish || bands.repeat) && <ScoreLegend />}
      <div className='grid gap-4 lg:grid-cols-3'>
        {leaderboards.map((list) => (
          <Leaderboard key={list.key} list={list} bands={bands} />
        ))}
      </div>
      {favoriteLists.length > 0 && (
        <div className='grid gap-4 md:grid-cols-2'>
          {favoriteLists.map((list) => (
            <Leaderboard key={list.key} list={list} />
          ))}
        </div>
      )}
      <HowWeCount
        items={[
          `Opens: opening a ${item} twice in a day counts once.`,
          "Rest days aren't counted.",
        ]}
        notes={[
          stats.truncated && 'This month had more items than we can list, so the least used ones are missing.',
          stats.updatedAt && !stats.isFinal && `Updated ${new Date(stats.updatedAt).toLocaleString()}.`,
        ]}
      />
    </div>
  )
}

// Same look as NotEnoughData, with a spinner
function LoadingCard({ text: [title, detail] }) {
  return (
    <div className='bg-white rounded-2xl px-6 py-8 text-center flex flex-col items-center'>
      <Spinner className='w-5 h-5 text-gray-200' />
      <p className='text-sm font-semibold text-tBlack mt-3'>{title}</p>
      {detail && <p className='text-sm text-gray-500 mt-2'>{detail}</p>}
    </div>
  )
}

// Shown where a list or the page has too little data, like Apple's App Analytics
function NotEnoughData({
  className = '',
  text = "Some items can't be included here because there isn't enough data yet.",
}) {
  return (
    <div className={`rounded-2xl px-6 py-8 text-center ${className}`}>
      <p className='text-sm font-semibold text-tBlack'>Not enough data</p>
      <p className='text-sm text-gray-500 mt-2'>{text}</p>
    </div>
  )
}

function HowWeCount({ items, notes = [] }) {
  const shownNotes = notes.filter(Boolean)
  return (
    <details className='text-[12.5px] text-gray-500'>
      <summary className='cursor-pointer font-semibold text-tBlack w-max'>How we count</summary>
      <ul className='list-disc pl-5 mt-2 flex flex-col gap-1 max-w-[720px]'>
        {[...items.filter(Boolean), ...shownNotes].map((text) => (
          <li key={text}>{text}</li>
        ))}
      </ul>
    </details>
  )
}

function EmptyMonth({ typeName, month }) {
  return (
    <p className='bg-white rounded-2xl px-7 py-5 text-tBlack'>
      No member activity on {typeName} in {getMonthLabel(month)}.
    </p>
  )
}

const percent = (value) => `${Math.round(value * 100)}%`

function Change({ current, previous }) {
  const change = getChange(current, previous)
  if (change === null) return null
  return (
    <>
      <span className={`font-semibold ${change >= 0 ? 'text-tGreen' : 'text-[#D08A2F]'}`}>
        {change >= 0 ? '▲' : '▼'} {Math.abs(Math.round(change))}%
      </span>{' '}
      vs last month
    </>
  )
}

// On wide screens every tile is the same arrow, notched on the left and pointing right. When the
// tiles stack they are plain rounded boxes.
const TILE_ARROW =
  'md:rounded-none md:pl-12 md:pr-12 md:py-6 md:[clip-path:polygon(0_0,calc(100%_-_36px)_0,100%_50%,calc(100%_-_36px)_100%,0_100%,36px_50%)]'

function FunnelTile({ icon, label, value, share, footer }) {
  return (
    <section aria-label={label} className={`bg-gray-100 rounded-[14px] px-4 py-4 min-w-0 ${TILE_ARROW}`}>
      <p className='flex items-center gap-2 text-[13px] text-gray-500'>
        <IconCircle icon={icon} size='sm' />
        {label}
      </p>
      <p className='text-3xl font-bold text-tBlack tabular-nums mt-2'>{value.toLocaleString()}</p>
      <span className='block h-1.5 rounded-full bg-gray-200 mt-3'>
        <span className='block h-1.5 rounded-full bg-tGreen' style={{ width: `${Math.max(2, share * 100)}%` }} />
      </span>
      {footer && <p className='text-xs text-gray-400 mt-2'>{footer}</p>}
    </section>
  )
}

// The conversion between two tiles, as plain text between the arrows
function FunnelStep({ rate, label }) {
  return (
    <div className='self-center flex flex-col items-center justify-center text-center text-[#14895F] w-28 md:w-[68px] py-1'>
      <span className='text-lg font-bold leading-none'>{percent(rate)}</span>
      <span className='text-[11px] leading-tight mt-1'>{label}</span>
    </div>
  )
}

function Funnel({ stats, rows, type, month }) {
  const totals = getTotals(rows, type)
  const previous = showsChange(type, month) ? stats.previous?.totals?.[type] || null : null
  const noun = type === 'video' ? 'Video' : 'Workout'
  const finishRate = getFinishRate({ openDays: totals.opens, completionDays: totals.finishes })
  const previousRate = previous ? getFinishRate({ openDays: previous.opens, completionDays: previous.finishes }) : null
  const share = (value) => (totals.opens > 0 ? value / totals.opens : 0)
  const change = (key) => previous && <Change current={totals[key]} previous={previous[key]} />
  return (
    <section aria-label={`${noun} funnel`} className='bg-white rounded-2xl px-5 py-5'>
      <div className='flex flex-wrap items-baseline justify-between gap-2 mb-4'>
        <h2 className='text-lg font-bold text-tBlack'>{noun} funnel</h2>
        <p className='text-xs text-gray-400'>
          {noun}s · {getMonthLabel(month)}
          {showsChange(type, month) &&
            ` · changes vs ${isCurrentMonth(month) ? 'the same days of ' : ''}${getPreviousMonthName(month)}`}
        </p>
      </div>
      <div className='grid gap-2 md:gap-0 grid-cols-1 md:grid-cols-[1fr_auto_1fr]'>
        <FunnelTile
          icon={ICONS.opens}
          label={`${noun} opens`}
          value={totals.opens}
          share={share(totals.opens)}
          footer={change('opens')}
        />
        <FunnelStep rate={finishRate} label='of opens finished' />
        <FunnelTile
          icon={ICONS.finishes}
          label={`${noun}s finished`}
          value={totals.finishes}
          share={share(totals.finishes)}
          footer={
            previous && (
              <>
                {change('finishes')}
                {previousRate !== null && ` · was ${percent(previousRate)} of opens`}
              </>
            )
          }
        />
      </div>
    </section>
  )
}

// Each takeaway's colour and icon, kept the same on its placeholder card
const TAKEAWAY_STYLES = {
  allRounder: { icon: FiStar, text: 'text-[#14895F]', circle: 'bg-[#E3F5EC] text-[#14895F]' },
  hiddenGem: { icon: TbDiamond, text: 'text-[#2F7FD1]', circle: 'bg-[#E6F0FB] text-[#2F7FD1]' },
  rarelyFinished: { icon: FiAlertCircle, text: 'text-[#C2410C]', circle: 'bg-[#FDECE4] text-[#C2410C]' },
}

// Each takeaway always has a card. When no workout fits, the card says what would.
const TAKEAWAY_CRITERIA = {
  allRounder: (item) => `A ${item} in your top 5 for opens, finishes and repeats.`,
  hiddenGem: (item) => `A ${item} members come back to a lot, but few open.`,
  rarelyFinished: (item) => `A popular ${item} that members finish far less often than your others.`,
}

function Takeaways({ takeaways, type }) {
  const item = type === 'video' ? 'video' : 'workout'
  const byKey = Object.fromEntries(takeaways.map((takeaway) => [takeaway.key, takeaway]))
  const placeholder = (key, eyebrow) => (
    <TakeawayCard
      key={key}
      eyebrow={eyebrow}
      kind={key}
      headline={`No ${item} fits yet`}
      body={TAKEAWAY_CRITERIA[key](item)}
      muted
    />
  )
  const allRounder = byKey.allRounder
  const hiddenGem = byKey.hiddenGem
  const rarelyFinished = byKey.rarelyFinished
  const common = (takeaway) => ({
    key: takeaway.key,
    headline: takeaway.row.title,
    program: takeaway.row.programId && takeaway.row.programName,
  })
  return (
    <div className='grid gap-4 lg:grid-cols-3'>
      {allRounder ? (
        <TakeawayCard
          {...common(allRounder)}
          eyebrow='All-rounder'
          kind='allRounder'
          body={`Top 5 on everything: #${allRounder.ranks.opens} opens, #${allRounder.ranks.finishes} finished, #${allRounder.ranks.repeats} repeats.`}
        />
      ) : (
        placeholder('allRounder', 'All-rounder')
      )}
      {hiddenGem ? (
        <TakeawayCard
          {...common(hiddenGem)}
          eyebrow='Hidden gem'
          kind='hiddenGem'
          body={`${(hiddenGem.row.repeatDays / hiddenGem.row.completers).toFixed(1)} repeats each by members who finished it, one of your highest. Only #${hiddenGem.ranks.opens} by opens. Worth featuring.`}
        />
      ) : (
        placeholder('hiddenGem', 'Hidden gem')
      )}
      {rarelyFinished ? (
        <TakeawayCard
          {...common(rarelyFinished)}
          eyebrow='Rarely finished'
          kind='rarelyFinished'
          body={`${percent(getFinishRate(rarelyFinished.row))} of opens end in a finish, vs ${percent(rarelyFinished.typicalFinish)} typical. Check length, difficulty or intro.`}
        />
      ) : (
        placeholder('rarelyFinished', 'Rarely finished')
      )}
    </div>
  )
}

function TakeawayCard({ eyebrow, kind, headline, program, body, muted = false }) {
  const { icon: Icon, text, circle } = TAKEAWAY_STYLES[kind]
  return (
    <section aria-label={eyebrow} className='bg-white rounded-2xl px-6 py-5 flex flex-col'>
      <p className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] ${text}`}>
        <span className={`w-7 h-7 rounded-full flex items-center justify-center ${circle}`}>
          <Icon className='w-3.5 h-3.5' strokeWidth={2} aria-hidden='true' />
        </span>
        {eyebrow}
      </p>
      <h3 className={`text-[17px] font-bold leading-snug mt-1.5 ${muted ? 'text-gray-400' : 'text-tBlack'}`}>
        {headline}
      </h3>
      {program && <p className='text-[12.5px] text-gray-400'>{program}</p>}
      <p className='text-[13px] leading-relaxed mt-1.5 text-gray-500'>{body}</p>
    </section>
  )
}

// Scores are plain text unless they stand out from the coach's other items
// Scores are text with a dot: green above typical, red below, grey otherwise
const SCORE_STYLES = {
  good: { text: 'text-[#14895F] font-semibold', dot: 'bg-[#1FB48F]' },
  neutral: { text: 'text-gray-600 font-semibold', dot: 'bg-gray-400' },
  bad: { text: 'text-[#C2410C] font-semibold', dot: 'bg-[#E8763A]' },
}

function Score({ band, children }) {
  const style = SCORE_STYLES[band] || SCORE_STYLES.neutral
  return (
    <span className={`inline-flex items-center gap-1.5 ${style.text}`}>
      {style.dot && <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} aria-hidden='true' />}
      {children}
    </span>
  )
}

function ScoreLegend() {
  return (
    <p className='flex flex-wrap items-center justify-end gap-3 text-[12.5px] text-gray-500'>
      <Score band='good'>above average</Score>
      <Score band='bad'>below average</Score>
      <Score band='neutral'>average</Score>
    </p>
  )
}

function Leaderboard({ list, bands }) {
  const max = list.items[0]?.value || 1
  const icon = ICONS[list.icon]
  return (
    <section aria-label={list.title} className='bg-white rounded-2xl px-6 pt-5 pb-3 flex flex-col min-w-0'>
      <div className='flex items-start gap-3 mb-2'>
        {icon && <IconCircle icon={icon} />}
        <div className='min-w-0 flex-1'>
          <div className='flex items-baseline justify-between gap-3'>
            <h2 className='text-lg font-bold text-tBlack'>{list.title}</h2>
          </div>
          {list.description && <p className='text-[12.5px] text-gray-500'>{list.description}</p>}
        </div>
      </div>
      {!list.items.length && <NotEnoughData className='bg-offWhite mt-2 mb-3' />}
      <ol className='flex flex-col'>
        {list.items.map(({ row, value }, index) => (
          <li key={row.key} className='grid grid-cols-[18px_1fr] gap-2.5 py-2.5 border-t border-offWhite'>
            <span className='text-xs text-gray-400 text-right pt-0.5'>{index + 1}</span>
            <span className='min-w-0'>
              <span className='block text-sm font-semibold text-tBlack'>{row.title}</span>
              {row.subtitle && <span className='block text-xs text-gray-400'>{row.subtitle}</span>}
              <span className='flex items-center gap-2 mt-1.5'>
                <span
                  className={`block h-2.5 rounded-full ${index === 0 ? 'bg-tGreen' : 'bg-tGreen/40'}`}
                  style={{ width: `${Math.max(2, (value / max) * 74)}%` }}
                />
                <span className='text-[13px] font-bold text-tBlack tabular-nums'>{list.format(value)}</span>
              </span>
              {bands && (
                <span className='flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 text-[12px]'>
                  {getRowScores(row, bands).map((score, i) => (
                    <React.Fragment key={score.key}>
                      {i > 0 && <span className='text-gray-300'>·</span>}
                      <Score band={score.band}>{score.label}</Score>
                    </React.Fragment>
                  ))}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

export default ContentStats
